import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { useSEO } from "../hooks/useSEO";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useSEO({
    title: "Research peptides catalogue · UK in stock",
    description:
      "Browse Sterling Bio's full UK catalogue of research peptides and bacteriostatic water — BPC-157, TB500, GHK-Cu, Semaglutide, Ipamorelin, NAD+, Epitalon, Semax, CJC-1295 and more. Free UK shipping. For laboratory research only.",
    path: "/products",
  });

  useEffect(() => {
    fetchProducts()
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch((err) => {
        // Show real error so production CORS / network issues are visible
        // instead of silently rendering an empty catalogue.
        toast.error("Could not load the catalogue. Please refresh in a moment.");
        // eslint-disable-next-line no-console
        console.error("fetchProducts failed:", err);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    return products
      .filter((p) => (category === "all" ? true : p.category === category))
      .filter((p) =>
        query.trim()
          ? `${p.name} ${p.dosage} ${p.sku}`.toLowerCase().includes(query.toLowerCase())
          : true,
      );
  }, [products, query, category]);

  const handleAdd = (p) => {
    addItem(p, 1);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div data-testid="products-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="border-b border-[#E2E8F0] pb-8 mb-10">
        <div className="sb-mono-label">Catalogue</div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold text-[#1B3A5C] tracking-tight" style={{ fontFamily: "Outfit" }}>
          All research peptides &amp; reagents
        </h1>
        <p className="mt-4 max-w-2xl text-[#475569]">
          {products.length > 0 ? `${products.length} products · ` : ""}Each item supplied lyophilised in sterile vials · UK stock · For laboratory
          research only.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-8">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="products-search"
            type="text"
            placeholder="Search by name, dosage or SKU..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none text-sm bg-white"
          />
        </div>
        <div className="flex gap-2">
          {[
            { id: "all", label: "All" },
            { id: "Peptide", label: "Peptides" },
            { id: "Solvent", label: "Solvents" },
          ].map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id.toLowerCase()}`}
              onClick={() => setCategory(c.id)}
              className={`px-4 py-2 text-sm font-medium tracking-wide border transition-colors ${
                category === c.id
                  ? "bg-[#1B3A5C] text-white border-[#1B3A5C]"
                  : "bg-white text-[#1B3A5C] border-[#E2E8F0] hover:border-[#1B3A5C]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#64748B]">Loading catalogue…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="products-empty" className="text-center py-20 text-[#64748B]">
          No products match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} onAdd={handleAdd} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Products;
